/** Voluum lander editor: load, General / Tracking tabs, save (PUT). */

import { type FC, useState } from 'react';
import type { VoluumPathUpdate } from '@trafficpulse/shared';

import { Button } from '@/components/ui/layout/Button';
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/layout/Drawer';
import { updateVoluumLander } from '@/features/flow-optimizer/services/voluum-lander-api';
import { useToast } from '@/providers/ToastProvider';

import { pathItemsSetLanderDisplayName } from '../path-item-operations';
import {
  buildVoluumLanderUpdateBody,
  parseVoluumLanderResource,
  type VoluumLanderFormValues,
} from './map-voluum-lander-form';
import { readHttpErrorMessage } from './read-http-error';
import {
  LanderEditTabBar,
  LanderGeneralTab,
  LanderTrackingTab,
  type LanderEditSubTab,
} from './tabs';
import { useVoluumLanderLoad } from './use-voluum-lander-load';

export interface PathLanderEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  landerId: string;
  rowIndex: number;
  onPatch: (mutator: (p: VoluumPathUpdate) => void) => void;
}

const PathLanderEditDialog: FC<PathLanderEditDialogProps> = ({
  open,
  onOpenChange,
  landerId,
  rowIndex,
  onPatch,
}) => {
  const toast = useToast();
  const [subTab, setSubTab] = useState<LanderEditSubTab>('general');
  const [values, setValues] = useState<VoluumLanderFormValues>(() =>
    parseVoluumLanderResource(undefined),
  );
  const [saving, setSaving] = useState(false);
  const { loading, loadError } = useVoluumLanderLoad(open, landerId, setValues);

  const patch = (p: Partial<VoluumLanderFormValues>) =>
    setValues((v) => ({ ...v, ...p }));

  const handleSave = async () => {
    const id = landerId.trim();
    if (!values.url.trim() || !values.namePostfix.trim()) {
      toast.error('Validation', 'URL and name postfix are required.');
      return;
    }
    setSaving(true);
    try {
      await updateVoluumLander(id, buildVoluumLanderUpdateBody(values));
      pathItemsSetLanderDisplayName(
        onPatch,
        rowIndex,
        values.namePostfix.trim(),
      );
      toast.success('Lander saved', 'Voluum lander updated.');
      onOpenChange(false);
    } catch (err: unknown) {
      toast.error('Save failed', readHttpErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const disableForm = loading || saving || Boolean(loadError);
  const t = values.fullNameReadOnly.trim();
  const title = t ? `Edit lander ${t}` : 'Edit lander';

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        variant="center"
        width="w-[min(520px,94vw)]"
        className="max-h-[90vh]"
      >
        <DrawerHeader className="space-y-0 px-5 py-3">
          <DrawerTitle className="text-center text-base leading-snug">
            {title}
          </DrawerTitle>
        </DrawerHeader>
        <DrawerBody className="max-h-[min(70vh,560px)] space-y-0 overflow-y-auto px-5 py-0">
          <LanderEditTabBar tab={subTab} onTab={setSubTab} />
          {loadError ? (
            <p className="py-3 text-xs text-destructive" role="alert">
              {loadError}
            </p>
          ) : null}
          {loading ? (
            <p className="py-3 text-xs text-muted-foreground">
              Loading from Voluum…
            </p>
          ) : null}
          {!loadError && !loading
            ? subTab === 'general'
              ? (
                  <LanderGeneralTab
                    values={values}
                    patch={patch}
                    disabled={disableForm}
                  />
                )
              : (
                  <LanderTrackingTab
                    values={values}
                    patch={patch}
                    disabled={disableForm}
                  />
                )
            : null}
        </DrawerBody>
        <DrawerFooter className="px-5 py-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={disableForm || Boolean(loadError) || loading}
            onClick={() => void handleSave()}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default PathLanderEditDialog;
